import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { listAdminContacts, fetchAdminCsv } from "../../lib/adminApi";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const Contacts = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAdminContacts({ limit: 1000, skip: 0 });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((x) =>
      [x.name, x.email, x.subject, x.message]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [items, q]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await fetchAdminCsv("/admin/contacts/export.csv");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contacts CSV exported");
    } catch (err) {
      toast.error(err.message || "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Contact Submissions">
      <div className="space-y-6" data-testid="admin-contacts-page">
        <Card>
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle>Contacts ({filtered.length})</CardTitle>
            <div className="flex gap-3">
              <Input
                placeholder="Search name, email, subject..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-72"
                data-testid="contacts-search"
              />
              <Button
                variant="outline"
                onClick={load}
                disabled={loading}
                data-testid="contacts-refresh"
              >
                Refresh
              </Button>
              <Button
                onClick={handleExport}
                disabled={exporting}
                data-testid="contacts-export"
              >
                {exporting ? "Exporting..." : "Export CSV"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-stone-500">Loading contacts...</p>
            ) : filtered.length === 0 ? (
              <p className="text-stone-500">No contact submissions yet.</p>
            ) : (
              <div className="space-y-4">
                {filtered.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border border-stone-200 p-4"
                    data-testid={`contact-row-${item.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                      <div>
                        <h3 className="font-semibold text-stone-900">{item.subject || "No Subject"}</h3>
                        <p className="text-sm text-stone-600">
                          {item.name} • {item.email}
                        </p>
                      </div>
                      <span className="text-xs text-stone-500">{formatDate(item.created_at)}</span>
                    </div>
                    <p className="mt-3 text-sm text-stone-700 whitespace-pre-wrap">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Contacts;
